import { useState } from 'react';
import { useCreate, useNotify } from 'react-admin';
import {
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';

import { PaperlessSettingsModal } from '@pumpkincare/shared';

import { stripeErrorStatus } from '../../../../constants/stripe-error-status';
import useBooleanInput from '../../../../hooks/useBooleanInput';
import RoutePaths from '../../../../routes';
import { createUserOnboardingTasks } from '../../../customers/service/customerService';
import { HDYHAU_OPTIONS, MY_VET_CLINIC, OTHER } from '../constants/HDYHAUQuestions';
import {
  createIdentity,
  postFinalizeAddAPetQuote,
  postFinalizeQuote,
  postIdentityHdyhau,
  putPetAddon,
  updateIdentity,
} from '../service/quoteFlowService';
import AddressDetails from './AddressDetails';
import BillingForm from './BillingForm';
import CheckoutSuccessModal from './CheckoutSuccessModal';
import PaymentForm from './PaymentForm';
import QuoteSummary from './QuoteSummary';
import ReferralForm from './ReferralForm';
import ShippingForm from './ShippingForm';

function CheckoutTab(props) {
  const { quote, edit, wellnessSelections = {} } = props;
  const stripe = useStripe();
  const [create] = useCreate();
  const elements = useElements();
  const skusToIgnore = ['embark-dna-kit'];
  const [shippingAddress, setShippingAddress] = useState({});
  const [HDYHAUAnswer, setHDYHAUAnswer] = useState();
  const [HDYHAUOther, setHDYHAUOther] = useState();
  const [HDYHAUVet, setHDYHAUVet] = useState();
  const [HDYHAUPumpkinNow, setHDYHAUPumpkinNow] = useState('');
  const [isLoading, toggleLoading] = useBooleanInput(false);
  const [isSuccessModalOpen, toggleSuccessModalOpen] = useBooleanInput(false);
  const [userId, setUserId] = useState();
  const [isPaymentEdit, togglePaymentEdit] = useBooleanInput(false);
  const [billingAddress, setBillingAddress] = useState({});
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] =
    useBooleanInput(true);

  const [nameOnCard, setNameOnCard] = useState();
  const [errors, setErrors] = useState({});
  const [phoneNumber, setPhoneNumber] = useState();
  const [isPaperlessSettingsModalOpen, togglePaperlessSettingsModal] =
    useBooleanInput(false);

  const notify = useNotify();

  const validShippingAddress = !!shippingAddress.address1 && !!shippingAddress.city;

  const validBillingAddress =
    isBillingSameAsShipping || (!!billingAddress.address1 && !!billingAddress.city);

  const isFormValid =
    (errors.cardNumber &&
      errors.expirationDate &&
      errors.cvc &&
      nameOnCard !== '' &&
      validShippingAddress &&
      phoneNumber &&
      validBillingAddress) ||
    quote.existingUserDetails;

  const isBilledAnnually =
    quote.billingOption === 'annually' ||
    !!quote.existingUserDetails?.is_charged_annually;

  function buildWellnessQuotePets() {
    return (quote.quote_pets || [])
      .filter(pet => {
        const tier = wellnessSelections[pet.id]?.tier;
        return tier && tier !== 'None';
      })
      .map(pet => ({
        pet_name: pet.name,
        species: pet.species,
        tier: `${wellnessSelections[pet.id].tier}-${pet.species}`,
        id: wellnessSelections[pet.id].wellnessQuotePetId,
      }));
  }

  function handleShipmentAddressChange(event) {
    setShippingAddress({ ...shippingAddress, [event.field]: event.value });
  }

  function handleBillingAddressChange(event) {
    setBillingAddress({ ...billingAddress, [event.field]: event.value });
  }

  function submitQuote(payload) {
    const wellnessQuotePets = buildWellnessQuotePets();
    const finalPayload = wellnessQuotePets.length
      ? { ...payload, wellness_quote_pets: wellnessQuotePets }
      : payload;

    postFinalizeAddAPetQuote(quote.id, finalPayload)
      .catch(e => {
        notify(`Unable to complete transaction: ${e.message}`, {
          type: 'warning',
        });
      })
      .finally(() => {
        toggleLoading(false);
      });
  }

  function handleHDYHAUChange(event) {
    setHDYHAUAnswer(event.target.value);
  }

  function onHDYHAUVetChange(event) {
    setHDYHAUVet(event);
  }

  async function handleSubmitValidateAddress(isPaperless) {
    toggleLoading();
    if (!quote.existingUserDetails) {
      try {
        await create(
          `${RoutePaths.addresses}/rating/validate`,
          {
            data: {
              street_1: shippingAddress.address1,
              street_2: shippingAddress.address2 || '',
              city: shippingAddress.city,
              zipcode: quote.policy_zipcode,
              state: quote.policy_state,
            },
          },
          { returnPromise: true }
        ).then(() => {
          const addressToBeSent = isBillingSameAsShipping
            ? shippingAddress
            : billingAddress;
          create(
            `${RoutePaths.addresses}/billing/validate`,
            {
              data: {
                country: addressToBeSent.country || 'US',
                street_1: addressToBeSent.address1,
                street_2: addressToBeSent.address2 || '',
                city: addressToBeSent.city,
                zipcode: addressToBeSent.zipcode
                  ? addressToBeSent.zipcode
                  : quote.policy_zipcode,
                state: addressToBeSent.state
                  ? addressToBeSent.state
                  : quote.policy_state,
              },
            },
            { returnPromise: true }
          )
            .then(() => {
              handleSubmitClick(isPaperless);
            })
            .catch(err => {
              toggleLoading();
              notify(err.message, {
                type: 'error',
              });
              return false;
            });
        });
      } catch (err) {
        toggleLoading();
        notify(err.message, { type: 'error' });
        return false;
      }
    } else {
      handleSubmitClick(isPaperless);
    }
  }

  async function submitIdentity() {
    let identityId = quote.identity_id;
    if (!identityId) {
      try {
        const identity = await Promise.resolve(createIdentity());
        identityId = identity.id;
      } catch (error) {
        console.error('Unable to create/update identity or submit HDYHAU', error);
      }
    }

    if (identityId) {
      const vetId = HDYHAUVet ? HDYHAUVet.id : quote.vet?.id;
      const session = uuid();

      const hdyhauPayload = [
        {
          quote_id: quote.id,
          order: -1,
          option_identifier: HDYHAUAnswer,
          option_value: HDYHAU_OPTIONS.find(option => option.value === HDYHAUAnswer)
            .label,
          vet_id: HDYHAUAnswer === MY_VET_CLINIC ? vetId : undefined,
          other: HDYHAUAnswer === OTHER ? HDYHAUOther : undefined,
          is_multi_touch: false,
          session,
          source: 'ADMIN:INS:HDYHAU',
        },
      ];

      hdyhauPayload.push({
        quote_id: quote.id,
        order: -1,
        option_identifier: 'PUMPKIN_NOW',
        option_value: 'PUMPKIN_NOW',
        session,
        is_multi_touch: false,
        other: HDYHAUPumpkinNow ? 'YES:ADMIN' : 'NO:ADMIN',
        source: 'ADMIN:INS:PUMPKIN_NOW',
      });

      Promise.all([
        updateIdentity(identityId, {
          ...quote,
          city: shippingAddress.city,
        }),
        postIdentityHdyhau(identityId, { hdyhau: hdyhauPayload }),
      ]);
    }
  }

  function handleSubmitClick(isPaperless) {
    let promises = [];
    quote.quote_pets.forEach((pet, qpIndex) => {
      let addonPayload = [];
      let removedAddon = false;
      // Iterate addons to find Embark kit and remove it
      pet.addons.forEach((addon, aIndex) => {
        if (skusToIgnore.indexOf(addon.addon.sku) > -1) {
          quote.quote_pets[qpIndex].addons.splice(aIndex, 1);
          removedAddon = true;
          return true;
        }
      });
      // Construct payload with existing non-embark addons
      if (removedAddon) {
        quote.quote_pets[qpIndex].addons.forEach(addon => {
          let addonId = addon.addon.id;
          addonPayload.push({
            addon: { id: addonId },
            installments: addon.installments,
          });
        });
        promises.push(
          putPetAddon(quote.id, quote.quote_pets[qpIndex].id, addonPayload)
        );
      }
    });
    // These promises must run before moving on to finalize to remove Embark addons from quote.
    Promise.all(promises)
      .then(() => {
        let billingAddressPayload, shippingAddressPayload, payload;
        if (quote.existingUserDetails) {
          if (isPaymentEdit) {
            if (errors.cardNumber === true && !nameOnCard) {
              notify(
                'Name on card is required when changing credit card information',
                { type: 'warning' }
              );
              toggleLoading(false);
              return;
            }
            const userShippingAddress = quote.existingUserDetails.rating_address;
            const userBillingAddress = quote.existingUserDetails.billing_address;

            billingAddressPayload = {
              country: billingAddress.country || userBillingAddress.country,
              street_1: billingAddress.address1 || userBillingAddress.street_1,
              street_2: billingAddress.address2 || userBillingAddress.street_2,
              city: billingAddress.city || userBillingAddress.city,
              zipcode: billingAddress.zipcode || userBillingAddress.zipcode,
              state: billingAddress.state || userBillingAddress.state_province,
              name_on_card: nameOnCard || quote.first_name + ' ' + quote.last_name,
            };
            shippingAddressPayload = {
              street_1: shippingAddress.address1 || userShippingAddress.street_1,
              street_2: shippingAddress.address2 || userShippingAddress.street_2,
              city: shippingAddress.city || userShippingAddress.city,
              zipcode: userShippingAddress.zipcode,
              state: userShippingAddress.state_province,
            };

            payload = {
              billing_information: billingAddressPayload,
              shipping_information: shippingAddressPayload,
              phone: phoneNumber || quote.existingUserDetails.phone,
              billed_annually: isBilledAnnually,
            };

            if (errors.cardNumber === true) {
              if (stripe) {
                stripe
                  .createToken(elements.getElement(CardNumberElement))
                  .then(response => {
                    if (response.error) {
                      notify(`[SOMETHING WENT WRONG]: ${response.error}`, {
                        type: 'error',
                      });
                    } else {
                      payload.stripeToken = response.token.id;
                      submitQuote(payload);
                    }
                  });
              } else {
                notify('Stripe is not initialized', { type: 'warning' });
              }
            } else {
              submitQuote(payload);
            }
          } else {
            const payload = { billed_annually: isBilledAnnually };
            submitQuote(payload);
          }
        } else {
          if (!HDYHAUAnswer) {
            notify('How did you hear about Pumpkin is a mandatory field', {
              type: 'warning',
            });
            toggleLoading(false);
            return;
          }

          if (HDYHAUAnswer === MY_VET_CLINIC && !quote.vet && !HDYHAUVet) {
            notify('Please select a veterinarian', { type: 'warning' });
            toggleLoading(false);
            return;
          }

          if (HDYHAUAnswer === OTHER && !HDYHAUOther) {
            notify('Please fill out a reason', { type: 'warning' });
            toggleLoading(false);
            return;
          }

          if (typeof HDYHAUPumpkinNow !== 'boolean') {
            notify('Please answer the PumpkinNow question', { type: 'warning' });
            toggleLoading(false);
            return;
          }

          submitIdentity();

          billingAddressPayload = {
            country: billingAddress.country || 'US',
            street_1: billingAddress.address1,
            street_2: billingAddress.address2 || '',
            city: billingAddress.city,
            zipcode: billingAddress.zipcode
              ? billingAddress.zipcode
              : quote.policy_zipcode,
            state: billingAddress.state ? billingAddress.state : quote.policy_state,
            name_on_card: nameOnCard,
          };
          shippingAddressPayload = {
            street_1: shippingAddress.address1,
            street_2: shippingAddress.address2 || '',
            city: shippingAddress.city,
            zipcode: quote.policy_zipcode,
            state: quote.policy_state,
          };

          if (stripe) {
            stripe
              .createToken(elements.getElement(CardNumberElement))
              .then(payload => {
                if (payload.error) {
                  throw new Error(payload.error?.message);
                } else {
                  postFinalizeQuote(
                    quote.id,
                    shippingAddressPayload,
                    isBillingSameAsShipping
                      ? {
                          ...shippingAddressPayload,
                          name_on_card: nameOnCard,
                        }
                      : billingAddressPayload,
                    phoneNumber,
                    payload.token.id,
                    isBilledAnnually,
                    isPaperless,
                    buildWellnessQuotePets()
                  )
                    .then(data => {
                      const userId = data.data.user.id;
                      setUserId(userId);
                      createUserOnboardingTasks(userId, {
                        quoteId: quote.id,
                      });
                      toggleSuccessModalOpen(true);
                    })
                    .catch(e => {
                      const errorMsg =
                        stripeErrorStatus[e?.message]?.error_description ||
                        e.message;
                      notify(`Unable to complete transaction: ${errorMsg}`, {
                        type: 'warning',
                      });
                    })
                    .finally(() => {
                      toggleLoading(false);
                    });
                }
              })
              .catch(e => {
                notify(`[SOMETHING WENT WRONG]: ${e}`, {
                  type: 'error',
                });
              });
          } else {
            notify('Stripe is not initialized', { type: 'warning' });
          }
        }
      })
      .catch(e => {
        notify(`[SOMETHING WENT WRONG]: ${e}`, {
          type: 'error',
        });
      });
  }

  function handleChangePumpkinNow(e) {
    setHDYHAUPumpkinNow(e.target.value === 'true');
  }

  return (
    <>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
      >
        <div style={{ width: '100%' }}>
          {quote.existingUserDetails ? (
            <>
              <Typography>
                Here is the existing address and payment method associated with this
                account.
              </Typography>
              <Typography style={{ color: '#FB5D3E' }}>
                If you would like to make changes, note that this will update for all
                Pet Plans.{' '}
                <span
                  style={{ color: 'blue', cursor: 'pointer' }}
                  onClick={togglePaymentEdit}
                >
                  Edit Details
                </span>
              </Typography>
            </>
          ) : null}

          {isPaymentEdit || !quote.existingUserDetails ? (
            <>
              <ShippingForm
                onPhoneNumberChange={setPhoneNumber}
                quote={quote}
                onAddressChange={handleShipmentAddressChange}
              />
              <Typography
                style={{
                  margin: '60px 0 0',
                  fontSize: '20px',
                  lineHeight: '24px',
                  fontWeight: '500',
                }}
              >
                Payment Information
              </Typography>
              <PaymentForm
                onNameCardChange={setNameOnCard}
                errors={errors}
                onErrorsChange={setErrors}
              />
              <BillingForm
                quote={quote}
                onAddressChange={handleBillingAddressChange}
                isBillingSameAsShipping={isBillingSameAsShipping}
                onSameAsShippingChange={setIsBillingSameAsShipping}
              />
            </>
          ) : (
            <AddressDetails existingUserDetails={quote.existingUserDetails} />
          )}

          {!quote.existingUserDetails ? (
            <>
              <ReferralForm
                onHDYHAUChange={handleHDYHAUChange}
                HDYHAUAnswer={HDYHAUAnswer}
                onHDYHAUVetChange={onHDYHAUVetChange}
                onHDYHAUOtherChange={setHDYHAUOther}
                quote={quote}
              />

              <Grid container flexDirection='row' alignItems='center' gap='16px'>
                <Typography>
                  Did PumpkinNow help convert this prospect?{''}
                  <sup style={{ color: '#B00020' }}>*</sup>
                </Typography>

                <FormControl component='fieldset'>
                  <RadioGroup
                    onChange={handleChangePumpkinNow}
                    value={HDYHAUPumpkinNow}
                    row
                  >
                    <FormControlLabel
                      value={true}
                      control={<Radio color='primary' />}
                      label='Yes'
                    />
                    <FormControlLabel
                      value={false}
                      control={<Radio data-testid={'pnow-false'} color='primary' />}
                      label='No'
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>
          ) : null}

          <Button
            color='primary'
            variant='contained'
            onClick={() => {
              if (!quote.existingUserDetails) {
                togglePaperlessSettingsModal();
              } else {
                handleSubmitValidateAddress();
              }
            }}
            style={{ marginTop: '32px', marginBottom: '32px', width: '100%' }}
            disabled={
              !isFormValid ||
              isLoading ||
              isSuccessModalOpen ||
              (quote.existingUserDetails &&
                !quote.existingUserDetails.payment_details?.last4)
            }
          >
            {isLoading ? <CircularProgress style={{ color: 'white' }} /> : 'SUBMIT'}
          </Button>
        </div>
        <div style={{ maxWidth: '500px', marginLeft: '24px' }}>
          <QuoteSummary
            edit={edit}
            quote={quote}
            skusToIgnore={skusToIgnore}
            wellnessSelections={wellnessSelections}
          />
        </div>
      </div>
      <CheckoutSuccessModal showDialog={isSuccessModalOpen} userId={userId} />
      {isPaperlessSettingsModalOpen ? (
        <PaperlessSettingsModal
          onSaveButtonClick={isPaperless => {
            handleSubmitValidateAddress(isPaperless);
            togglePaperlessSettingsModal();
          }}
          saveButtonLabel='Submit'
          emailAddress={quote.email}
          onClose={togglePaperlessSettingsModal}
        />
      ) : null}
    </>
  );
}

CheckoutTab.propTypes = {
  edit: PropTypes.func,
  wellnessSelections: PropTypes.object,
  quote: PropTypes.shape({
    id: PropTypes.string,
    billingOption: PropTypes.string,
    email: PropTypes.string,

    existingUserDetails: PropTypes.shape({
      is_charged_annually: PropTypes.bool,
      billing_address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state_province: PropTypes.string,
        zipcode: PropTypes.string,
        country: PropTypes.string,
      }),
      rating_address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state_province: PropTypes.string,
        zipcode: PropTypes.string,
      }),
      phone: PropTypes.string,
      payment_details: PropTypes.shape({
        last4: PropTypes.string,
        brand: PropTypes.string,
      }),
    }),
    identity_id: PropTypes.string,

    policy_state: PropTypes.string,
    policy_zipcode: PropTypes.string,
    quote_pets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        addons: PropTypes.arrayOf(
          PropTypes.shape({
            addon: PropTypes.shape({
              id: PropTypes.string,
              sku: PropTypes.string,
            }),
            installments: PropTypes.any,
          })
        ),
      })
    ),
  }),
};

export default CheckoutTab;
